<template>
  <div>
    <div v-if="context === 'onlinebooking' " class="close-button btn-back" @click="back">
      <font-awesome-icon :icon="['fas', 'caret-left']" class="text-grey" />&nbsp;Back
    </div>
    <div v-if="context === 'onlinebooking' || context === 'enquirylogin'" class="close-button btn-close" @click="closeModal">
      Close&nbsp; <Cross color="grey" :width="14" :height="14" alt="close" class="close-button btn-close cross" />
    </div>

    <div :class="{'login_window' : context === 'onlinebooking' || context === 'enquirylogin', 'login_window_client_area' : context === 'clientarea', 'background-white' : context === 'clientarea'}">
      <div v-if="context === 'onlinebooking' || context === 'enquirylogin'">
        <h1 class="h3 login_head uppercase" v-html="showSignUpForm ? 'Add your details' : 'Create an Account'" />
      </div>

      <div v-else-if="context === 'clientarea'">
        <h1 class="h3 login_head client-area-head" v-html="showSignUpForm ? 'Add your details' : 'Create an Account'" />
      </div>

      <transition name="fade" mode="out-in">
        <div>
          <!-- Login - Step 1 -->
          <form key="1" @submit.prevent="SignUp">
            <div v-show="showSignUpForm">
              <div>
                <p class="form__box--label">
                  First name
                </p>
                <input v-model="firstname"
                       v-validate="'required'"
                       placeholder="Please enter your first name"
                       type="text"
                       name="firstname"
                       :class="{ 'error-highlight': errors.has('firstname'), 'credentials': true }"
                       label="First name"
                       @keydown="resetError()"
                >
              </div>

              <div>
                <p class="form__box--label">
                  Last name
                </p>
                <input v-model="lastname"
                       v-validate="'required'"
                       placeholder="Please enter your last name"
                       type="text"
                       name="lastname"
                       :class="{ 'error-highlight': errors.has('lastname'), 'credentials': true }"
                       label="Last name"
                       @keydown="resetError()"
                >
              </div>

              <div>
                <p class="form__box--label">
                  Email
                </p>
                <input v-model="email"
                       v-validate="'required|email'"
                       placeholder="Please enter your email"
                       type="text"
                       name="email"
                       :class="{ 'error-highlight': errors.has('email'), 'credentials': true }"
                       label="Email address"
                       @keydown="resetError()"
                >
              </div>

              <div>
                <p class="form__box--label" style="float:left">
                  Password
                </p>
                <button type="button" style="float:right" @click="switchPasswordVisibility">
                  <div v-show="passwordFieldType === 'password'" class="text-light-blue">
                    <font-awesome-icon :icon="['fas', 'eye']" size="sm" style="color: $highlight-blue" aria-label="eye" class="link-unstyle " />
                    Show
                  </div>
                  <div v-show="passwordFieldType === 'text'" class="text-light-blue">
                    <font-awesome-icon :icon="['fas', 'eye-slash']" size="sm" style="color: $highlight-blue" aria-label="eye" class="link-unstyle " />
                    &nbsp;Hide
                  </div>
                </button>
                <input v-model="password"
                       v-validate="{ required: true, regex: /^(?=.*[0-9])(?=.*[a-zA-Z])(?=\S+$).{8,50}$/ }"
                       placeholder="Please enter a secure password"
                       label="Password"
                       :type="passwordFieldType"
                       name="password"
                       :class="{ 'error-highlight': errors.has('password'), 'credentials': true }"
                       @keydown="resetError()"
                >
                <div class="text-grey text-center">
                  (Min 8 characters including 1 number)
                </div>
              </div>

              <div class="login_errors">
                <span v-if="errorText" style="color:red" v-html="errorText" />
              </div>

              <div>
                <label for="terms" class="checkbox-label text-dark">
                  <p>I agree to the <app-link id="104" type="page">Terms of Use</app-link> and <app-link id="76" type="page">Privacy Policy</app-link>.</p>
                  <input id="terms" v-model="terms" v-validate="'required:true'" type="radio" name="terms" value="yes">
                  <span :class="{'checkbox-checkmark': true, 'checkbox-error-highlight': errors.has('terms') }" />
                </label>
              </div>

              <div class="login_buttons">
                <button v-if="submitting === false"
                        type="submit"
                        class="btn-primary-dark-md"
                >
                  Continue
                </button>
                <button v-else
                        disabled
                        class="btn-primary-dark-md"
                >
                  Signing up ...
                </button>
              </div>
            </div>

            <p>Already have an account? <a class="cursor" href="" @click.prevent="showLogin()">Log in</a></p>

            <div v-show="!showSignUpForm">
              <div class="login_buttons">
                <button type="button" class="btn-primary-dark-md" @click="showSignUpForm = !showSignUpForm">
                  Sign up with email
                </button>
              </div>
            </div>
          </form>

          <div v-show="!showSignUpForm">
            <p class="uppercase login_or semi-bold">
              or
            </p>

            <div class="form__social">
              <button class="btn-secondary-dark-sm" @click="doFacebookLogin">
                <div :style="{'visibility': (faceBookLoading ? 'hidden' : '')}">
                  <font-awesome-icon :icon="['fab', 'facebook-f']" size="lg" style="color: $darkBlack" aria-label="facebook" class="link-unstyle form__social--icon" />
                  Log in with Facebook
                </div>
                <svg :style="{'visibility': (faceBookLoading ? '' : 'hidden')}" class="spinner" viewBox="0 0 100 100">
                  <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="5" />
                </svg>
              </button>

              <button class="btn-secondary-dark-sm" @click="doGoogleLogin">
                <div :style="{'visibility': (googleLoading ? 'hidden' : '')}">
                  <font-awesome-icon :icon="['fab', 'google']" size="lg" style="color: $darkBlack" aria-label="google" class="link-unstyle form__social--icon" />
                  Log in with Google
                </div>
                <svg :style="{'visibility': (googleLoading ? '' : 'hidden')}" class="spinner" viewBox="0 0 100 100">
                  <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="5" />
                </svg>
              </button>

              <button class="btn-secondary-dark-sm" @click="logInWithApple">
                <div :style="{'visibility': (appleLoading ? 'hidden' : '')}">
                  <font-awesome-icon :icon="['fab', 'apple']" size="lg" style="color: $darkBlack" aria-label="apple" class="link-unstyle form__social--icon" />
                  Log in with Apple
                </div>
                <svg :style="{'visibility': (appleLoading ? '' : 'hidden')}" class="spinner" viewBox="0 0 100 100">
                  <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="5" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

import Bus from '../../events/bus';
import SocialLogins from '../../mixins/socialLogins';
import Cross from '../icons/Cross.vue';

export default {
  components: {
    Cross,
  },
  mixins: [SocialLogins],
  props: {
    context: { type: String, default: 'clientarea' }, // clientarea or onlinebooking or enquirylogin
  },
  data() {
    return {
      submitting: false,
      serverErrorMsg: null,
      firstname: null,
      lastname: null,
      password: null,
      email: null,
      terms: false,
      showSignUpForm: false,
      passwordFieldType: 'password',
    };
  },
  computed: {
    errorText() {
      let errtxt = '';
      if (this.errors && this.errors.items.length > 0) {
        const required = this.errors.items.filter((e) => e.rule === 'required' && e.field !== 'terms');
        const email = this.errors.items.filter((e) => e.rule === 'email');
        const password = this.errors.items.filter((e) => e.rule === 'regex' && e.field === 'password');
        const requiredTermsCheck = this.errors.items.filter((e) => e.rule === 'required' && e.field === 'terms');

        let reqCount = 0;
        let reqMsg = '';
        required.forEach((r) => { reqMsg += `${r.field}, `; reqCount++; });

        let emailMsg = '';
        email.forEach((r) => { emailMsg += `${r.field}, `; });

        let reqTermsMsg = '';
        if (requiredTermsCheck.length > 0) reqTermsMsg = 'Please agree to the Terms of Use.';

        let passwordMsg = '';
        if (password.length > 0) passwordMsg = 'Your password does not meet the security requirements stated above.';

        if (reqMsg != '') {
          if (reqCount > 1) reqMsg = `The ${reqMsg.slice(0, -2)} fields are all required.`;
          else reqMsg = `The ${reqMsg.slice(0, -2)} field is required.`;
        }

        if (emailMsg != '') emailMsg = `The ${emailMsg.slice(0, -2)} field must be a valid email.`;

        errtxt = reqMsg;

        if (emailMsg != '') {
          errtxt += (reqMsg != '') ? (`<br>${emailMsg}`) : emailMsg;
        }
        if (passwordMsg != '') {
          errtxt += (errtxt != '') ? (`<br>${passwordMsg}`) : passwordMsg;
        }
        if (reqTermsMsg != '') {
          errtxt += (errtxt != '') ? (`<br>${reqTermsMsg}`) : reqTermsMsg;
        }
      }

      if (this.serverErrorMsg && this.serverErrorMsg != '') {
        errtxt += (errtxt != '') ? (`<br>${this.serverErrorMsg}`) : this.serverErrorMsg;
      }
      return errtxt;
    },
  },
  methods: {
    SignUp(e) {
      e.preventDefault();
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.submitting = true;
          const contact = {
            email: this.email,
            firstname: this.firstname,
            lastname: this.lastname,
            password: this.password,
            ga_ClientId: (typeof google_tag_manager !== 'undefined') ? google_tag_manager['GTM-WD9Z68'].dataLayer.get('cid') : '',
            ga_SessionId: this.$store.getters['users/getGASessionId'],
          };
          console.log('contact', contact);
          this.$store.dispatch('users/createAccountAndLogIn', contact).then((res) => {
            this.$emit('usersigned', res.contact.id);
          }).catch((err) => {
            if (err.message && err.message.indexOf('401') > -1) {
              // status code 401 - meaning the email address already exist
              this.serverErrorMsg = 'It appears that this email address is already registered. Please try another email or log in below.';
            } else {
              // unknown unexpected error occurred
              this.serverErrorMsg = 'Account creation failed due to an unexpected error.';
            }
            this.submitting = false;
          });
        }
      });
    },
    resetError() {
      this.serverErrorMsg = null;
    },
    showLogin() {
      this.$emit('showLoginClicked');
    },
    back() {
      if (this.showSignUpForm) {
        this.showSignUpForm = false;
      } else {
        this.closeModal();
      }
    },
    closeModal() {
      this.$emit('closeModal');
    },
    doFacebookLogin() {
      this.logInWithFacebook('leadbooker'); // SocialLogins mixin
    },
    doGoogleLogin() {
      this.loginWithGoogle('leadbooker'); // SocialLogins mixin
    },
    switchPasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
  },
};
</script>

<style lang="scss" scoped>
   @import "../../assets/styles/settings/config.scss";

  .login_window_client_area {
    width: 400px;
    text-align: center;
    padding: 20px 30px;
    @include mobile{
      height: 90vh;
      width: 100%;
    }
  }

  .client-area-head {
    text-align: center;
    margin: 0 auto 12px;
    line-height: 1.2;
    font-size: 28px;
    max-width: 295px;
  }

  .login_window {
    padding: 2em 10px 10px;
  }

  h1 {
    font-size: 16px;
    margin-bottom: 0;
  }

  .wave-grey {
    margin: 0.5em auto 1em;
  }

  .login_buttons button {
    width: 100%;
    margin-bottom: 10px;
  }

  .login_or {
    margin: 10px 0 20px;
    text-align: center;
  }

  .login_reset {
    div {
      margin-bottom: 1em;
    }
  }

.checkbox-label {
    margin-top: 10px;
    padding-left: 16px;
    font-size: 0.9em;
}
  .login_errors {
    text-align: center;
    margin-bottom: 20px;
    color: red;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s ease;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .credentials {
    border: 1px solid $grey;
    height: 50px;
    color: $black;
    width: 100%;
    margin-bottom: 0.5em;
  }

  .checkbox-error-highlight {
    border: 1px solid red;
}

  .btn-back {
    text-align:left;
    float:left
  }

  .btn-close {
    float:right;
    & img {
      height: 10px;
    }
  }
  .cross {
    margin-top: 5.5px
  }

  .spinner {
  animation: rotate 2s linear infinite;
  z-index: 2;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -25px 0 0 -25px;
  width: 50px;
  height: 50px;

  & .path {
    stroke: $darkBlack;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }

}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

</style>
